/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-01 11:39:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { Page, PageActions, Layout, Stack, Button } from '@shopify/polaris';
import { connect } from 'react-redux';
import { reduxForm, reset, FieldArray } from 'redux-form';
import { withI18n } from '@shopify/react-i18n';

import { URL } from 'src/constants/links';
import StopWordsList from './stop-words-list';
import DidYouKnow from 'src/components/views/stop-words/did-you-know';
import ImportModal from './import';
import ExportModal from './export';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import TypeFormEmbedded from 'src/components/views/common/typeform-embedded';
import YoutubeModal from 'src/components/views/common/youtube-modal';

import { confirmLeavePage } from 'src/helpers';
import { buildInitialStopWordsValues, buildStopWordsValues } from 'src/helpers/stop-words-helper';
import * as StopWordsActions from 'actions/stop-words-actions';
import * as quickStartGuideAction from 'actions/dashboard/quick-start-guide-action';

import type { ComposedProps, State } from './index.type';
import TitleHelp from 'src/components/views/common/title-help';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';
import { routes } from 'src/routes';
import history from 'src/routes/history';

class StopWords extends Component<ComposedProps, State> {
  timeoutProcessingUpdateBanner = undefined;
  state = {
    // Import modal
    activeImportModal: false,

    // Export modal
    activeExportModal: false,
    discardState: 0,

    youtubeModal: false
  };

  componentDidMount() {
    const { getStopWords } = this.props;

    getStopWords();

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  render() {
    const {
      i18n,
      dirty,
      submitting,
      handleSubmit,
      pristine,
      loading,
      updateSuccess,
      selectedPlan
    } = this.props;
    const { discardState, youtubeModal } = this.state;

    return (
      <div className="BC-Page BC-Page__StopWords">
        <Page
          title={i18n.translate('AdminHTML.StopWords.StopWords.title')}
          primaryAction={
            <div className="BC-Page BC-Page__Button">
              <Stack>
                {!pristine && (
                  <Button disabled={submitting} onClick={this.discardChange}>
                    {i18n.translate('AdminHTML.Common.Action.cancel')}
                  </Button>
                )}
                <Button
                  primary
                  onClick={handleSubmit(this.handleSave.bind(this))}
                  disabled={pristine || submitting}
                  loading={loading && !pristine}>
                  {i18n.translate('AdminHTML.StopWords.StopWords.save')}
                </Button>
              </Stack>
            </div>
          }>
          <TitleHelp
            i18n={i18n}
            onPlay={this.handleOpenYoutubeModal}
            hrefUserManual={URL['Link_V2_Search_StopWords_ManualGuide']}
            onAnalytics={() => history.push(routes.analytics.overviewSearch())}
            onImport={this.handleChangeImportModal}
            onExport={this.handleChangeExportModal}
          />

          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Banner */}
                <DidYouKnow />
              </div>
            </Layout.Section>

            {/* List of stop words*/}
            <div className="BC-stop-words-list-wrapper">
              <FieldArray name="stopWords" component={StopWordsList} />
            </div>
          </Layout>

          <PageActions
            primaryAction={{
              content: i18n.translate('AdminHTML.StopWords.StopWords.save'),
              onAction: handleSubmit(this.handleSave.bind(this)),
              disabled: pristine || submitting,
              loading: loading && !pristine
            }}
            secondaryActions={
              !pristine
                ? [
                    {
                      content: i18n.translate('AdminHTML.Common.Action.cancel'),
                      onAction: this.discardChange,
                      disabled: submitting
                    }
                  ]
                : []
            }
          />
        </Page>
        <ImportModal
          active={this.state.activeImportModal}
          handleChange={this.handleChangeImportModal}
          getStopWords={this.props.getStopWords}
          history={history}
        />

        <ExportModal
          active={this.state.activeExportModal}
          handleChange={this.handleChangeExportModal}
        />

        {!_.isEmpty(selectedPlan) && (
          <TypeFormEmbedded
            type="stop-words"
            conditionShow={updateSuccess}
            conditionShowStateChange={discardState}
          />
        )}

        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['Link_V2_Search_StopWords_Video']}
          title={i18n.translate('AdminHTML.StopWords.StopWords.youtubeModal_Title')}
          onClose={this.handleCloseYoutubeModal}
        />

        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={dirty}
          // Navigate function
          navigate={path => history.push(path)}
          shouldBlockNavigation={dirty}
        />
      </div>
    );
  }

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  // Handle click save button
  handleSave = async data => {
    const { updateStopWords, markDoneStep } = this.props;
    const saveData = buildStopWordsValues(data.stopWords);

    await updateStopWords(saveData);

    markDoneStep('V2_SEARCH1', {
      isDone: true
    });

    Emitter.emit(EVENT_KEY.RELOAD_CSAT_ADMIN_APP_DISPLAY);
  };

  // Handle discard button
  discardChange = () => {
    const { resetForm } = this.props;
    const { discardState } = this.state;

    resetForm();
    this.setState({ discardState: discardState + 1 });
  };

  // Hide/show import modal
  handleChangeImportModal = () => {
    this.setState(({ activeImportModal }) => ({
      activeImportModal: !activeImportModal
    }));
  };

  // Hide/show export modal
  handleChangeExportModal = () => {
    this.setState(({ activeExportModal }) => ({
      activeExportModal: !activeExportModal
    }));
  };

  handleOpenYoutubeModal = () => this.setState({ youtubeModal: true });
  handleCloseYoutubeModal = () => this.setState({ youtubeModal: false });

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { dirty, i18n } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };
}

const StopWordsForm = reduxForm({
  form: 'StopWordsForm', // a unique identifier for this form
  enableReinitialize: true
})(StopWords);

const mapDispatchToProps = (dispatch, ownProps) => ({
  getStopWords: () => dispatch(StopWordsActions.getStopWords(ownProps.i18n)),
  updateStopWords: (stopWords: Object) =>
    dispatch(StopWordsActions.updateStopWords(stopWords, ownProps.i18n)),
  resetForm: () => dispatch(reset('StopWordsForm')),
  markDoneStep: (stepName, value) =>
    dispatch(quickStartGuideAction.markDoneStep(stepName, value, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const { stopWords } = state;

  return {
    getLoading: stopWords.getLoading,
    loading: stopWords.updateLoading,
    initialValues: buildInitialStopWordsValues(stopWords.stopWords),
    importing: _.get(stopWords, 'importing', false),
    updateSuccess: _.get(stopWords, 'updateSuccess', false),
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(StopWordsForm));
